import React, { useEffect } from "react";
import { connect } from "react-redux";
import * as shopActions from "../actions/shop";
import { bindActionCreators } from "redux";
import moment from "moment";
import { useTranslation } from "react-i18next";

import Preloader from "../components/Preloader/Preloader";
import Order from "../components/Order/Order";
import PaginationOrders from "../components/Shop/PaginationOrders/PaginationOrders";
import formatPrice from "../helpers/formatPrice";
import download from "../images/download.png";
import DeliveryDate from "../components/DeliveryDate/DeliveryDate";

import "./scss/Orders.scss";

const Orders = (props) => {
  const { t } = useTranslation();
  document.title = `Gudema - ${t("orderHistory")}`;

  const { getOrders, changeReady } = props;

  useEffect(() => {
    getOrders();

    return () => {
      changeReady(false);
    };
  }, [getOrders, changeReady]);

  const returnFalse = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const downloadOrders = () => {
    const link = document.createElement("a");
    link.href = "/api/orders/orders.pdf";
    link.setAttribute("download", "orders.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPaymentStatusText = (status) => {
  switch (String(status).trim().toUpperCase()) {
    case "PAID":
    case "PAIDED":
    case "PAID_FULL":
    case "APMOKETA":
    case "TRUE":
      return t("paymentStatusText.paid");

    case "NOT_PAID":
    case "UNPAID":
    case "FALSE":
      return t("paymentStatusText.unpaid");

    case "UNDERPAID":
    case "PARTLY_PAID":
    case "PARTIAL":
      return t("paymentStatusText.partial");

    case "OVERPAID":
      return t("paymentStatus.OVERPAID");

    case "WAITING":
    case "PENDING":
      return t("paymentStatusText.waiting");

    default:
      return status == null || status === ""
        ? "—"
        : String(status);
  }
};

  const getOrderStatusText = (status) => {
    switch (String(status)) {
      case "IN_PROGRESS":
        return t("orderStatusText.inProgress");

      case "DELIVERED":
        return t("orderStatusText.delivered");

      case "CANCELED":
        return t("orderStatusText.canceled");

      case "PENDING":
        return t("orderStatusText.pending");

      case "REFUNDED":
        return t("orderStatusText.refunded");

      default:
        return t("orderStatusText.pending");
    }
  };

  const getOrderStatusClass = (status) => {
    switch (String(status)) {
      case "IN_PROGRESS":
        return "orders-badge--progress";

      case "DELIVERED":
        return "orders-badge--delivered";

      case "CANCELED":
        return "orders-badge--canceled";

      case "PENDING":
        return "orders-badge--pending";

      case "REFUNDED":
        return "orders-badge--refunded";

      default:
        return "orders-badge--pending";
    }
  };

  if (props.order) {
    return (
      <main className="orders-page">
        <div className="orders-page__container">
          <Order />
        </div>
      </main>
    );
  }

  return (
    <main className="orders-page">
      <div className="orders-page__container">
        <DeliveryDate className="orders-page__delivery-date" />

        {!props.isReady && !props.orders ? (
          <Preloader />
        ) : props.orders && props.orders.length ? (
          <>
            <div className="orders-card">
              <div className="orders-table">
                <div className="orders-table__head">
                  <div>{t("ordersPage.id")}</div>
                  <div>{t("ordersPage.nr")}</div>
                  <div>{t("ordersPage.invoiceCreated")}</div>
                  <div>{t("ordersPage.paymentStatus")}</div>
                  <div>{t("ordersPage.plannedDeliveryDate")}</div>
                  <div>{t("ordersPage.status")}</div>
                  <div>{t("ordersPage.total")}</div>
                </div>

                {props.orders.map((item, i) => (
                  <div
                    className="orders-table__row"
                    key={i}
                    onClick={props.getOrder.bind(
                      this,
                      item.id,
                      item.invoiceLink
                    )}
                  >
                    <div>{item.id}</div>

                    <div>
                      {!item.invoiceLink ? (
                        item.invoiceNo || ""
                      ) : (
                        <a href={item.invoiceLink} onClick={returnFalse}>
                          {item.invoiceNo || ""}
                        </a>
                      )}
                    </div>

                    <div>
                      {item.invoiceDate
                        ? moment(item.invoiceDate).format("YYYY-MM-DD")
                        : ""}
                    </div>

                    <div>
                      <span className="orders-badge orders-badge--payment">
                        {getPaymentStatusText(item.paidStatus)}
                      </span>
                    </div>

                    <div>
                      {item.plannedDeliveryDate
                        ? moment(item.plannedDeliveryDate).format("YYYY-MM-DD")
                        : ""}
                    </div>

                    <div>
                      <span
                        className={`orders-badge ${getOrderStatusClass(
                          item.status
                        )}`}
                      >
                        {getOrderStatusText(item.status)}
                      </span>
                    </div>

                    <div>
                      {item.totalSum ? formatPrice(item.totalSum) : ""}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="orders-card__download"
                onClick={downloadOrders}
              >
                <img src={download} alt="download" />
                {t("ordersPage.downloadPdf")}
              </button>
            </div>

            <PaginationOrders
              getOrders={props.getOrders}
              page={props.pageOrder}
              pages={props.pagesOrder}
              data={props.orders}
              setPages={props.setPagesOrder}
              filterParams={props.filterParamsOrder}
              setLimitOffset={props.setLimitOffsetOrder}
            />
          </>
        ) : (
          <div className="orders-page__empty">{t("ordersPage.empty")}</div>
        )}
      </div>
    </main>
  );
};

const mapStateToProps = (state) => ({
  orders: state.shop.orders,
  order: state.shop.order,
  isReady: state.shop.isReady,
  perPageOrder: state.shop.perPageOrder,
  pageOrder: state.shop.pageOrder,
  pagesOrder: state.shop.pagesOrder,
  itemsTotalOrder: state.shop.itemsTotalOrder,
  filterParamsOrder: state.shop.filterParamsOrder,
});

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(shopActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Orders);
